// src/App.js - Updated main application with routing
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation, useNavigate } from 'react-router-dom';
import LoginForm from './components/LoginForm';
import RegisterForm from './components/RegisterForm';
import OAuthCallback from './components/OAuthCallback';
import Dashboard from './components/Dashboard';
import apiService from './services/api';
import { ThemeProvider } from './contexts/ThemeContext';

// Wrapper component to extract portfolioId from URL params
const DashboardWithPortfolio = ({ user, onLogout }) => {
  const { portfolioId } = useParams();
  return <Dashboard user={user} onLogout={onLogout} portfolioId={portfolioId} />;
};

// Wrapper component to extract accountId from URL params
const DashboardWithAccount = ({ user, onLogout }) => {
  const { accountId } = useParams();
  return <Dashboard user={user} onLogout={onLogout} accountId={accountId} />;
};

// Inner app component that has access to Router context
const AppContent = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRegister, setShowRegister] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuthStatus = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const userData = await apiService.getCurrentUser();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        console.error('Auth check failed:', error);
        apiService.logout();
        // Clear the URL params when logging out
        navigate('/');
      }
    }
    setLoading(false);
  };

  const handleLogin = async (credentials) => {
    try {
      const response = await apiService.login(credentials.username, credentials.password);
      if (response && response.user) {
        setUser(response.user);
        setIsAuthenticated(true);

        // Restore previous location if it exists
        const savedPath = sessionStorage.getItem('redirectPath');
        if (savedPath && savedPath !== '/') {
          sessionStorage.removeItem('redirectPath');
          navigate(savedPath);
        }

        return { success: true };
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const handleRegister = async (userData) => {
    try {
      const response = await apiService.register(userData);
      if (response && response.user) {
        setUser(response.user);
        setIsAuthenticated(true);

        // Restore previous location if it exists
        const savedPath = sessionStorage.getItem('redirectPath');
        if (savedPath && savedPath !== '/') {
          sessionStorage.removeItem('redirectPath');
          navigate(savedPath);
        }

        return { success: true };
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const handleSocialAuth = async (provider) => {
    try {
      // Get OAuth URLs from backend
      const urls = await apiService.getSocialAuthUrls();
      const authUrl = urls[provider];

      if (!authUrl) {
        console.error('No auth URL found for provider:', provider);
        alert(`No OAuth URL configured for ${provider}`);
        return;
      }

      // Redirect to OAuth provider
      window.location.href = authUrl;
    } catch (error) {
      console.error('Social auth failed:', error);
      alert(`Social auth error: ${error.message}`);
    }
  };

  const handleLogout = () => {
    // Save current path to restore after re-login if desired
    if (location.pathname !== '/' || location.search) {
      sessionStorage.setItem('redirectPath', location.pathname + location.search);
    }
    
    apiService.logout();
    setIsAuthenticated(false);
    setUser(null);
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Check for OAuth callback routes BEFORE authentication check
  if (location.pathname.startsWith('/auth/')) {
    return <OAuthCallback />;
  }

  if (!isAuthenticated) {
    // Save current location to restore after login
    if ((location.pathname !== '/' || location.search) && !sessionStorage.getItem('redirectPath')) {
      sessionStorage.setItem('redirectPath', location.pathname + location.search);
    }

    if (showRegister) {
      return (
        <RegisterForm
          onRegister={handleRegister}
          onSocialAuth={handleSocialAuth}
          onSwitchToLogin={() => setShowRegister(false)}
        />
      );
    }

    return (
      <LoginForm
        onLogin={handleLogin}
        onSocialAuth={handleSocialAuth}
        onSwitchToRegister={() => setShowRegister(true)}
      />
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Dashboard user={user} onLogout={handleLogout} />} />
      <Route path="/dashboard" element={<Dashboard user={user} onLogout={handleLogout} />} />
      <Route path="/portfolio/:portfolioId" element={<DashboardWithPortfolio user={user} onLogout={handleLogout} />} />
      <Route path="/accounts/:accountId" element={<DashboardWithAccount user={user} onLogout={handleLogout} />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

const App = () => {
  return (
    <ThemeProvider>
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
};

export default App;

